import React, { useEffect, useState } from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { StepProps, ApiOccasion, BackendOccasionType } from '@/src/types';
import { cn } from '@/src/infrastructure/lib/utils';
import { motion } from 'motion/react';
import { getOccasions } from '@/src/infrastructure/lib/api';

const OCCASION_ICON: Record<BackendOccasionType, keyof typeof Icons> = {
  WEDDING: 'Wedding',
  BIRTHDAY: 'Birthday',
  ANNIVERSARY: 'Party',
  SEND_OFF: 'Graduation',
  BABY_SHOWER: 'BabyShower',
  ENGAGEMENT: 'Wedding',
  CUSTOM: 'Other',
};

export function StepOccasion({ onNext, data, updateData }: StepProps) {
  const [occasions, setOccasions] = useState<ApiOccasion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    getOccasions()
      .then((res) => setOccasions(res.occasions ?? []))
      .catch((err) => setError(err.message ?? 'Failed to load occasions.'))
      .finally(() => setLoading(false));
  }, []);

  const selected = data.occasionType;

  return (
    <div className="w-full flex flex-col items-center">
      <div className="text-center mb-12">
        <h1 className="text-4xl lg:text-5xl font-bold text-on-surface mb-4">What are we celebrating?</h1>
        <p className="text-on-surface-variant font-medium">Select an occasion to find the perfect starting point.</p>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-10 h-10 rounded-full border-4 border-primary border-t-transparent animate-spin" />
        </div>
      )}

      {!loading && error && (
        <p className="text-status-failed font-medium mb-8">{error}</p>
      )}

      {!loading && !error && (
        <>
          {data.templateId && (
            <div className="w-full max-w-2xl mb-8 p-4 rounded-2xl border border-amber-500/30 bg-amber-500/10 text-amber-800 dark:text-amber-200 text-sm text-center">
              <p className="font-semibold text-amber-900 dark:text-amber-300">Occasion is set for this draft ({data.occasionType})</p>
              <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-200/80">
                To create an invitation for a different occasion, please start a new invitation from your dashboard. This draft will remain untouched.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 w-full mb-16">
            {occasions.map((occasion) => {
              const iconKey = OCCASION_ICON[occasion.type] ?? 'Other';
              const Icon = Icons[iconKey] ?? Icons.Other;
              const isSelected = selected === occasion.type;

              return (
                <motion.button
                  key={occasion.type}
                  whileHover={{ scale: data.templateId ? 1 : 1.02 }}
                  whileTap={{ scale: data.templateId ? 1 : 0.98 }}
                  disabled={Boolean(data.templateId)}
                  onClick={() => {
                    if (data.templateId) return;
                    updateData({ occasionType: occasion.type });
                    onNext();
                  }}
                  className={cn(
                    'flex flex-col items-center justify-center p-8 rounded-[2rem] border-2 transition-all shadow-sm',
                    isSelected
                      ? 'border-primary bg-primary/5 ring-4 ring-primary/10'
                      : 'border-surface-low bg-surface hover:bg-surface-low hover:border-outline-variant',
                    data.templateId && 'opacity-60 cursor-not-allowed'
                  )}
                >
                  <div className={cn(
                    'w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors',
                    isSelected ? 'bg-primary text-white' : 'bg-surface-low text-on-surface-variant',
                  )}>
                    <Icon size={32} />
                  </div>
                  <span className={cn(
                    'text-base font-bold',
                    isSelected ? 'text-primary' : 'text-on-surface',
                  )}>
                    {occasion.label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </>
      )}

    </div>
  );
}
